<script setup>
import { ref } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import GlobalFooter from "@/components/GlobalFooter.vue";
import { useRouter } from "vue-router";
import { UseProductStore } from "@/stores/productStore";
import { UseMoneyStore } from "@/stores/moneyStore";
import { UseCourierStore } from "@/stores/courierStore";
import { UseOrderStore } from "@/stores/orderStore";
import { VueSpinner } from "vue3-spinners";
import { sendOrderData } from "@/service/orderService";
import { useI18n } from 'vue-i18n'
import { useLocalization } from '@/locales/localization';

const { currentLocale, updateLocale } = useLocalization();

const { t, locale } = useI18n()

const router = useRouter();
const orderStore = UseOrderStore();
const productStore = UseProductStore();
const moneyStore = UseMoneyStore();
const courierStore = UseCourierStore();

const message = ref("");
const apiMessage = ref("");
const loading = ref(false);

// Validation
const rules = { order: { required }, email: { required } };
const v$ = useVuelidate(rules, orderStore);

// Form handlers
const goToProductsPage = async () => {
  message.value = "";
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  loading.value = true;
  apiMessage.value = "";

  try {
    const responseData = await sendOrderData({
      order: orderStore.order,
      email: orderStore.email,
    }, locale.value);

    console.log("API Response:", responseData);

    productStore.$reset();
    moneyStore.$reset();
    courierStore.$reset();

    await router.push("/products");
  } catch (error) {
    console.error("API Error:", error);
    apiMessage.value = error.message;
    message.value = t('returnRequest.invalidCombination');
  } finally {
    loading.value = false;
  }
};

const inputOrder = () => {
  clearMessage()
  orderStore.order = orderStore.order.replace(/\s+/g, '');
}

const inputContact = () => {
  clearMessage()
  orderStore.email = orderStore.email.replace(/\s+/g, '');
}

const clearMessage = () => {
  message.value = "";
  apiMessage.value="";
}
</script>


<template>
  <div class="flex flex-col justify-center items-center min-h-screen overflow-hidden relative">
    
    <div class="flex-grow flex flex-col items-center">
      <div class="place-items-center justify-center mt-10">
        <img src="@/assets/tfp_logo.svg" alt="TFP logo" class="h-12 w-auto"/>
      </div>

      
      <h2 class="text-center responsive-title-4 font-bold mt-10 mb-10">{{ t('returnRequest.title') }}</h2>
      <section class="grid place-content-center">
        <div class="border login-container bg-background-form mb-5 py-4 h-auto">
          <form class="py-5 px-12">
            <!-- Order Number -->
            <div>
              <label for="order" class="responsive-title-7 text-bw-black">{{ t('returnRequest.orderNumber') }}</label>
              <br/>
              <input type="text" id="order" v-model="orderStore.order" @input="inputOrder()" class="mt-1 pl-1 w-full focus:outline-black" />
              <span v-if="v$.order.$error" class="text-primary-red responsive-body-small text-center"><br>
                {{ t('returnRequest.orderNumberError') }}
              </span>
            </div>

            <!-- Email/Mobile -->
            <div class="mt-3 mb-5">
              <label for="email" class="responsive-title-7 text-bw-black">{{ t('returnRequest.emailOrMobile') }}</label>
              <br/>
              <input type="text" id="email" v-model="orderStore.email" @input="inputContact()" class="mt-1 pl-1 w-full focus:outline-black"/>
              <span v-if="v$.email.$error" class="text-primary-red responsive-body-small "><br>{{ t('returnRequest.emailOrMobileError') }}</span>
            </div>

            <p v-if="apiMessage" class="text-primary-red responsive-body-small text-center mt-2 text-wrap">
              {{ apiMessage }}
            </p>

            <div v-if="!loading" class="grid place-items-center w-full">
              <button
                  @click.prevent="goToProductsPage"
                  class="border rounded bg-bw-black responsive-body-small text-bw-white mt-2 py-2 w-full hover:bg-bw-darkGrey focus:outline-2 focus:outline-offset-2 focus:outline-bw-black"
                  :class="v$.$invalid ? 'bg-bw-darkGrey' : 'bg-bw-black'"
              >
                {{ t('returnRequest.searchOrder') }}
              </button>
            </div>

            <div v-if="loading" class="grid place-content-center">
              <VueSpinner size="20"/>
            </div>
          </form>
        </div>

        <!-- Language Switcher (Centered & Visible) -->
        <div class="flex justify-center my-2">
          <div
              class="flex items-center gap-3 px-4 py-2 border rounded-full bg-white shadow-md cursor-pointer hover:shadow-lg transition"
              @click="updateLocale(currentLocale === 'el' ? 'en' : 'el')" 
          >
          <img
              :src="currentLocale === 'el'
                ? require('@/assets/flags/flag-greece.svg')
                : require('@/assets/flags/flag-uk.svg')"
              :alt="currentLocale === 'el' ? 'Greek' : 'English'"
              class="w-6 h-6"
          />
          <span class="responsive-body-small">
            {{ currentLocale === 'el' ? 'Ελληνικά' : 'English' }}
          </span>
        </div>
          
        </div>


        <!-- Help Section -->
        <div class="help-container grid place-content-center place-items-center text-center responsive-body-small text-bw-black bg-background-contact p-4 mt-5 max-w-md mx-auto">
          <div class="font-bold">{{ t('help.needHelp') }}</div>
          <br/>
          <text>{{ t('help.contactText') }} <a class="font-bold whitespace-nowrap focus:outline-2 focus:outline-offset-2 focus:outline-bw-black" href="mailto:support@thefashionproject.gr">support@thefashionproject.gr</a></text>
        </div>
      </section>
    </div>
    <GlobalFooter></GlobalFooter>
  </div>
</template>

